import React, { useState } from 'react';
import { FiPlay, FiInfo, FiBook, FiVideo, FiFileText } from 'react-icons/fi';
import MaterialViewModal from './MaterialViewModal';
import { normalizeImageUrl } from '../../utils/imageUtils';

// Importando as imagens padrão
import defaultVideoThumb from '../../assets/images/thumbnails/thumb-video.webp';
import defaultBookThumb from '../../assets/images/thumbnails/thumb-livro.webp';
import defaultMagazineThumb from '../../assets/images/thumbnails/thumb-revista.webp';

interface Material {
  id: string;
  titulo: string;
  descricao: string;
  tipo: 'vídeo' | 'livro' | 'revista';
  iframe: string;
  thumb_url: string | null;
}

interface MaterialCardProps {
  material: Material;
  showButtons?: boolean;
}

const MaterialCard: React.FC<MaterialCardProps> = ({ material, showButtons = true }) => {
  const [showModal, setShowModal] = useState(false);
  const [showContent, setShowContent] = useState(false);
  
  const openModal = () => {
    setShowModal(true);
    setShowContent(false);
  };
  
  const closeModal = () => {
    setShowModal(false);
    setTimeout(() => {
      setShowContent(false);
    }, 300); // Aguarda o fim da animação para resetar o estado
  };
  
  const playContent = () => {
    setShowContent(true);
  };
  
  // Renderizar ícone apropriado para o tipo de material
  const renderIcon = () => {
    switch (material.tipo) {
      case 'vídeo':
        return <FiVideo size={32} className="text-blue-400" />;
      case 'livro':
        return <FiBook size={32} className="text-green-400" />;
      case 'revista':
        return <FiFileText size={32} className="text-purple-400" />;
      default:
        return <FiPlay size={32} className="text-vermelho-claro" />;
    }
  };
  
  // Obter a imagem padrão apropriada baseada no tipo de material
  const getDefaultThumbnail = () => {
    switch (material.tipo) {
      case 'vídeo':
        return defaultVideoThumb;
      case 'livro':
        return defaultBookThumb;
      case 'revista':
        return defaultMagazineThumb;
      default:
        return defaultVideoThumb;
    }
  };
  
  return (
    <>
      <div 
        className="group relative bg-gray-900 rounded-lg overflow-hidden cursor-pointer transform transition-all duration-300 hover:-translate-y-2 hover:shadow-lg hover:shadow-vermelho-escuro/10"
        onClick={openModal}
      >
        {/* Thumbnail do material - mantendo proporção 16:9 exata */}
        <div className="aspect-w-16 aspect-h-9 bg-gray-800">
          {material.thumb_url ? (
            <img 
              src={normalizeImageUrl(material.thumb_url) || getDefaultThumbnail()} 
              alt={material.titulo} 
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              onError={(e) => {
                console.log('Erro ao carregar imagem:', material.thumb_url);
                (e.target as HTMLImageElement).src = getDefaultThumbnail();
              }}
            />
          ) : (
            <img 
              src={getDefaultThumbnail()} 
              alt={material.titulo} 
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          )}
          
          {/* Informações e botões (aparecem apenas no hover) */}
          <div className="absolute bottom-0 left-0 right-0 p-3 transform translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
            <div className="bg-black/70 p-2 rounded backdrop-blur-sm">
              <h3 className="text-white font-semibold mb-1 line-clamp-1">{material.titulo}</h3>
              
              {showButtons && (
                <div className="flex space-x-2 mt-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openModal();
                    }}
                    className="flex-1 py-1 px-2 bg-gray-800/80 hover:bg-gray-700 text-white text-xs rounded flex items-center justify-center backdrop-blur-sm"
                  >
                    <FiInfo className="mr-1" /> Detalhes
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openModal();
                      setTimeout(() => playContent(), 10);
                    }}
                    className="flex-1 py-1 px-2 bg-vermelho-claro hover:bg-vermelho-claro/90 text-white text-xs rounded flex items-center justify-center"
                  >
                    <FiPlay className="mr-1" /> Assistir
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Modal de visualização */}
      {showModal && (
        <MaterialViewModal
          material={material}
          showContent={showContent}
          onClose={closeModal}
          onPlayContent={playContent}
        />
      )}
    </>
  );
};

export default MaterialCard; 